export type PermissionAction =
  | 'CREATE'
  | 'READ'
  | 'UPDATE'
  | 'DELETE'
  | 'MANAGE';

// User role constants that map to the UsersRole enum in Prisma
export const USER_ROLES = {
  ADMIN: 'ADMIN',
  PRINCIPAL: 'PRINCIPAL',
  TEACHER: 'TEACHER',
  STUDENT: 'STUDENT',
  STAFF: 'STAFF',
  PARENT: 'PARENT',
} as const;

export type UserRoleType = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// Helper function to check if a role is an admin role
export function isAdminRole(role: string): boolean {
  return role === USER_ROLES.ADMIN;
}

// DEV NOTE : this is being used in create role drawer to map module and permissions
// update shuold be reflected in : packages/frontend/src/components/settings/RolesAndPermissions/CreateRoleDrawer.tsx:21
export type PermissionModule =
  | 'SETTINGS'
  | 'STUDENTS'
  | 'STAFF'
  | 'TRANSPORTATION'
  | 'FEES'
  | 'REPORTS'
  | 'GRADES'
  | 'FEEDBACKS' // Added new module
  | 'NOTICES' // Added new module
  | 'EXAM'; // Added new module

export interface Permission {
  id: string;
  name: string;
  description: string;
  module: PermissionModule;
  action: PermissionAction;
}

export interface Role {
  id: string;
  name: string;
  description: string;
  isSystem?: boolean;
  permissions: string[];
  permissionCount?: number;
  assignedCount: number;
  assignedUsers?: Array<{
    id: string;
    name: string;
    email: string;
    avatar: string | null;
  }>;
}

export interface ModulePermission {
  id: string;
  name: string;
  description: string;
  permissions: Permission[];
}

export interface Designation {
  id: string;
  name: string;
  description: string;
  assignedCount: number;
  assignedUsers: Array<{
    id: string;
    name: string;
    email: string;
    avatar: string | null;
  }>;
}

// use in permission based rendering?
export const BASE_PERMISSIONS = {
  SETTINGS: {
    READ: 'settings.read',
    MANAGE: 'settings.manage',
  },
  STAFF: {
    CREATE: 'staff.create',
    READ: 'staff.read',
    UPDATE: 'staff.update',
    DELETE: 'staff.delete',
  },
  STUDENTS: {
    CREATE: 'students.create',
    READ: 'students.read',
    UPDATE: 'students.update',
    DELETE: 'students.delete',
  },
  GRADES: {
    READ: 'grades.read',
    CREATE: 'grades.create',
    UPDATE: 'grades.update',
    DELETE: 'grades.delete',
  },
  TRANSPORTATION: {
    CREATE: 'transportation.create',
    READ: 'transportation.read',
    UPDATE: 'transportation.update',
    DELETE: 'transportation.delete',
  },
  FEES: {
    CREATE: 'fees.create',
    READ: 'fees.read',
    UPDATE: 'fees.update',
    DELETE: 'fees.delete',
  },
  REPORTS: {
    MANAGE: 'reports.manage',
  },
  FEEDBACKS: {
    READ: 'feedbacks.read',
    UPDATE: 'feedbacks.update',
  },
  NOTICES: {
    READ: 'notices.read',
    CREATE: 'notices.create',
    // UPDATE: 'notices.update',
    DELETE: 'notices.delete',
  },
  //DEV CHECK: need to ensure these changes for exam module
  // EXAM: {
  //   CREATE: 'exam.create',
  //   READ: 'exam.read',
  //   UPDATE: 'exam.update',
  //   DELETE: 'exam.delete',
  // },
} as const;

// DEV NOTE : this is being used in module permissions to map module and permissions as per each role
export const DEFAULT_MODULE_PERMISSIONS: ModulePermission[] = [
  {
    id: 'settings',
    name: 'Settings Management',
    description: 'Manage system settings and configurations',
    permissions: [
      {
        id: BASE_PERMISSIONS.SETTINGS.READ,
        name: 'View Settings',
        description: 'View system settings',
        module: 'SETTINGS',
        action: 'READ',
      },
      {
        id: BASE_PERMISSIONS.SETTINGS.MANAGE,
        name: 'Manage Settings',
        description: 'Full control over system settings',
        module: 'SETTINGS',
        action: 'MANAGE',
      },
    ],
  },
  {
    id: 'staff',
    name: 'Staff Management',
    description: 'Manage staff records and information',
    permissions: [
      {
        id: BASE_PERMISSIONS.STAFF.CREATE,
        name: 'Add Staff',
        description: 'Add new staff to the system',
        module: 'STAFF',
        action: 'CREATE',
      },
      {
        id: BASE_PERMISSIONS.STAFF.READ,
        name: 'View Staff',
        description: 'View staff profiles and information',
        module: 'STAFF',
        action: 'READ',
      },
      {
        id: BASE_PERMISSIONS.STAFF.UPDATE,
        name: 'Edit Staff',
        description: 'Modify staff information',
        module: 'STAFF',
        action: 'UPDATE',
      },
      {
        id: BASE_PERMISSIONS.STAFF.DELETE,
        name: 'Delete Staff',
        description: 'Remove staff from the system',
        module: 'STAFF',
        action: 'DELETE',
      },
    ],
  },
  {
    id: 'students',
    name: 'Student Management',
    description: 'Manage student records and information',
    permissions: [
      {
        id: BASE_PERMISSIONS.STUDENTS.CREATE,
        name: 'Add Students',
        description: 'Add new students to the system',
        module: 'STUDENTS',
        action: 'CREATE',
      },
      {
        id: BASE_PERMISSIONS.STUDENTS.READ,
        name: 'View Students',
        description: 'View student profiles and information',
        module: 'STUDENTS',
        action: 'READ',
      },
      {
        id: BASE_PERMISSIONS.STUDENTS.UPDATE,
        name: 'Edit Students',
        description: 'Modify student information',
        module: 'STUDENTS',
        action: 'UPDATE',
      },
      {
        id: BASE_PERMISSIONS.STUDENTS.DELETE,
        name: 'Delete Students',
        description: 'Remove students from the system',
        module: 'STUDENTS',
        action: 'DELETE',
      },
    ],
  },
  {
    id: 'grades',
    name: 'Grade Management',
    description: 'Manage student grades',
    permissions: [
      {
        id: BASE_PERMISSIONS.GRADES.READ,
        name: 'View Grades',
        description: 'View student grades',
        module: 'GRADES',
        action: 'READ',
      },
      {
        id: BASE_PERMISSIONS.GRADES.CREATE,
        name: 'Create Grades',
        description: 'Create student grades',
        module: 'GRADES',
        action: 'CREATE',
      },
      {
        id: BASE_PERMISSIONS.GRADES.UPDATE,
        name: 'Update Grades',
        description: 'Update student grades',
        module: 'GRADES',
        action: 'UPDATE',
      },
      {
        id: BASE_PERMISSIONS.GRADES.DELETE,
        name: 'Delete Grades',
        description: 'Delete student grades',
        module: 'GRADES',
        action: 'DELETE',
      },
    ],
  },
  {
    id: 'transportation',
    name: 'Transportation',
    description: 'Manage transportation system',
    permissions: [
      {
        id: BASE_PERMISSIONS.TRANSPORTATION.CREATE,
        name: 'Add Transportation',
        description: 'Add new transportation records',
        module: 'TRANSPORTATION',
        action: 'CREATE',
      },
      {
        id: BASE_PERMISSIONS.TRANSPORTATION.READ,
        name: 'View Transportation',
        description: 'View transportation information',
        module: 'TRANSPORTATION',
        action: 'READ',
      },
      {
        id: BASE_PERMISSIONS.TRANSPORTATION.UPDATE,
        name: 'Edit Transportation',
        description: 'Modify transportation information',
        module: 'TRANSPORTATION',
        action: 'UPDATE',
      },
      {
        id: BASE_PERMISSIONS.TRANSPORTATION.DELETE,
        name: 'Delete Transportation',
        description: 'Remove transportation records',
        module: 'TRANSPORTATION',
        action: 'DELETE',
      },
    ],
  },
  {
    id: 'fees',
    name: 'Fees Management',
    description: 'Manage fee structure and collections',
    permissions: [
      {
        id: BASE_PERMISSIONS.FEES.CREATE,
        name: 'Create Fees',
        description: 'Create new fee structures and records',
        module: 'FEES',
        action: 'CREATE',
      },
      {
        id: BASE_PERMISSIONS.FEES.READ,
        name: 'View Fees',
        description: 'View fee information and records',
        module: 'FEES',
        action: 'READ',
      },
      {
        id: BASE_PERMISSIONS.FEES.UPDATE,
        name: 'Update Fees',
        description: 'Modify fee structures and records',
        module: 'FEES',
        action: 'UPDATE',
      },
      {
        id: BASE_PERMISSIONS.FEES.DELETE,
        name: 'Delete Fees',
        description: 'Remove fee structures and records',
        module: 'FEES',
        action: 'DELETE',
      },
    ],
  },
  {
    id: 'reports',
    name: 'Reports',
    description: 'Generate and manage reports',
    permissions: [
      {
        id: BASE_PERMISSIONS.REPORTS.MANAGE,
        name: 'Manage Reports',
        description: 'Full control over reports system',
        module: 'REPORTS',
        action: 'MANAGE',
      },
    ],
  },
  {
    id: 'suggestionsComplaints', // New module
    name: 'Suggestions & Complaints',
    description: 'Manage suggestions and complaints',
    permissions: [
      {
        id: BASE_PERMISSIONS.FEEDBACKS.READ,
        name: 'View Suggestions & Complaints',
        description: 'View all suggestions and complaints',
        module: 'FEEDBACKS',
        action: 'READ',
      },
      {
        id: BASE_PERMISSIONS.FEEDBACKS.UPDATE,
        name: 'Update Suggestions & Complaints',
        description: 'Update existing suggestions or complaints',
        module: 'FEEDBACKS',
        action: 'UPDATE',
      },
    ],
  },
  {
    id: 'notices', // New module
    name: 'Notices Management',
    description: 'Manage notices and announcements',
    permissions: [
      {
        id: BASE_PERMISSIONS.NOTICES.READ,
        name: 'View Notices',
        description: 'View all notices',
        module: 'NOTICES',
        action: 'READ',
      },
      {
        id: BASE_PERMISSIONS.NOTICES.CREATE,
        name: 'Create Notices',
        description: 'Create new notices',
        module: 'NOTICES',
        action: 'CREATE',
      },
      {
        id: BASE_PERMISSIONS.NOTICES.DELETE,
        name: 'Delete Notices',
        description: 'Delete notices',
        module: 'NOTICES',
        action: 'DELETE',
      },
    ],
  },
  //DEV CHECK: need to ensure these changes for exam module
  // {
    // id: 'exam', // New module
    // name: 'Exam Management',
    // description: 'Manage exams and assessments',
    // permissions: [
    //   {
    //     id: BASE_PERMISSIONS.EXAM.CREATE,
    //     name: 'Create Exams',
    //     description: 'Create new exams and assessments',
    //     module: 'EXAM',
    //     action: 'CREATE',
    //   },
    //   {
    //     id: BASE_PERMISSIONS.EXAM.READ,
    //     name: 'View Exams',
    //     description: 'View exam information and records',
    //     module: 'EXAM',
    //     action: 'READ',
    //   },
    //   {
    //     id: BASE_PERMISSIONS.EXAM.UPDATE,
    //     name: 'Update Exams',
    //     description: 'Modify exam information and records',
    //     module: 'EXAM',
    //     action: 'UPDATE',
    //   },
    //   {
    //     id: BASE_PERMISSIONS.EXAM.DELETE,
    //     name: 'Delete Assessments',
    //     description: 'Remove exam records',
    //     module: 'EXAM',
    //     action: 'DELETE',
    //   },
    // ],
  // },
];

// not being used
export const DEFAULT_ROLES: Partial<Role>[] = [
  {
    name: 'ADMIN',
    description: 'Administrator with full system access',
    isSystem: true,
    permissions: DEFAULT_MODULE_PERMISSIONS.flatMap((m) =>
      m.permissions.map((p) => p.id),
    ),
  },
  {
    name: 'PRINCIPAL',
    description: 'Principal with elevated permissions',
    isSystem: true,
    permissions: [
      BASE_PERMISSIONS.SETTINGS.READ,
      BASE_PERMISSIONS.STAFF.READ,
      BASE_PERMISSIONS.STAFF.UPDATE,
      BASE_PERMISSIONS.STUDENTS.READ,
      BASE_PERMISSIONS.STUDENTS.UPDATE,
      BASE_PERMISSIONS.GRADES.READ,
      BASE_PERMISSIONS.GRADES.CREATE,
      BASE_PERMISSIONS.GRADES.UPDATE,
      BASE_PERMISSIONS.GRADES.DELETE,
      //DEV CHECK: need to ensure these changes for exam module
      // BASE_PERMISSIONS.EXAM.READ,
      // BASE_PERMISSIONS.EXAM.CREATE,
      // BASE_PERMISSIONS.EXAM.UPDATE,
      // BASE_PERMISSIONS.EXAM.DELETE,
      // BASE_PERMISSIONS.REPORTS.MANAGE,
      // BASE_PERMISSIONS.DOCUMENTS.READ,
      // BASE_PERMISSIONS.USERS.READ
    ],
  },
  {
    name: 'TEACHER',
    description: 'Regular teaching staff',
    isSystem: true,
    permissions: [
      BASE_PERMISSIONS.STUDENTS.CREATE,
      BASE_PERMISSIONS.STUDENTS.READ,
      BASE_PERMISSIONS.STUDENTS.UPDATE,
      BASE_PERMISSIONS.STUDENTS.DELETE,
      BASE_PERMISSIONS.STAFF.READ,
      BASE_PERMISSIONS.GRADES.READ,
      BASE_PERMISSIONS.GRADES.CREATE,
      BASE_PERMISSIONS.GRADES.UPDATE,
      //DEV CHECK: need to ensure these changes for exam module
      // BASE_PERMISSIONS.EXAM.READ, 
      // BASE_PERMISSIONS.EXAM.CREATE,
      // BASE_PERMISSIONS.EXAM.UPDATE,
      // BASE_PERMISSIONS.USERS.READ
    ],
  },
  // {
  //     name: 'STUDENT',
  //     description: 'Student with limited access',
  //     isSystem: true,
  //     permissions: [
  //         BASE_PERMISSIONS.STUDENTS.READ,
  //         BASE_PERMISSIONS.GRADES.READ,
  //         BASE_PERMISSIONS.USERS.READ
  //     ]
  // },
  {
    name: 'PARENT',
    description: "Parent/Guardian with access to their children's information",
    isSystem: true,
    permissions: [
      // BASE_PERMISSIONS.STUDENTS.READ,
      // BASE_PERMISSIONS.GRADES.READ
    ],
  },
];

// not being used
export const DEFAULT_DESIGNATIONS = [
  { name: 'Principal', description: 'School Principal' },
  { name: 'Vice Principal', description: 'Vice Principal' },
  { name: 'Head of Department', description: 'Department Head' },
  { name: 'Senior Teacher', description: 'Senior Teaching Staff' },
  { name: 'Teacher', description: 'Teaching Staff' },
  { name: 'Administrative Officer', description: 'Admin Staff' },
  { name: 'Transport Manager', description: 'Transportation Staff' },
];
